// nQube IDE — a full Eclipse Theia workspace on a Python block's repository,
// in the browser. /orgs/:orgId/ws/:workspaceId/pipelines/:pipelineId/ide
// (?block=<id> opens that block's repo straight away).
//
// The backend runs one Theia task per user on demand (shared/nqube/ide.js)
// and proxies it under /nqube/ide/<id>/w/<token>/ (api/nqubeIde.js). Members
// get an edit workspace (commit + push from Source Control or the terminal;
// git credentials are fetched through nQube when git asks, nothing is stored
// on the volume), viewers a read-only one. Per-user editor settings (theme,
// autosave off, font, split direction) live in ideSettings/{uid} and apply
// to the next workspace opened. Ported from the kit's components/ide/IdePanel.
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Link, useParams, useSearchParams } from 'react-router-dom';
import { useEntityDoc } from '../data/entities';
import { apiGet, apiPost, backendBase } from '../utils/backendApi';

const TOKEN_KEY = (id) => `nv.nqube.ide.${id}`;
const PHASE_LABEL = { queued: 'Queued', launch: 'Starting the workspace task', init: 'Cloning the repository', ready: 'Ready', failed: 'Failed', stopped: 'Stopped' };
const ago = (ms) => { if (!ms) return '—'; const m = Math.round((Date.now() - ms) / 60000); return m < 1 ? 'just now' : m < 60 ? `${m} min ago` : `${Math.round(m / 60)} h ago`; };

async function apiPut(path, body) {
  const { auth } = await import('../firebaseConfig');
  const token = await auth.currentUser.getIdToken();
  const res = await fetch(`${backendBase()}${path}`, { method: 'PUT', headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` }, body: JSON.stringify(body) });
  const data = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(data?.error || `HTTP ${res.status}`);
  return data;
}

function StatusTag({ w }) {
  const tone = w.status === 'ready' ? 'ok' : w.status === 'starting' ? 'info' : w.status === 'error' ? 'danger' : '';
  return <span className={`mf-chip${tone ? ` is-${tone}` : ''}`}>{w.status === 'starting' ? (PHASE_LABEL[w.phase] || 'Starting') : w.status}</span>;
}

function SettingsForm({ onSaved }) {
  const [s, setS] = useState(null);
  const [busy, setBusy] = useState(false);
  const [note, setNote] = useState('');
  useEffect(() => { apiGet('/nqube/ide/settings').then((r) => setS(r.settings)).catch((e) => setNote(e.message)); }, []);
  if (!s) return <p className="cs-help" style={{ fontSize: 12 }}>{note || 'Loading settings…'}</p>;
  const set = (k, v) => setS({ ...s, [k]: v });
  const save = async () => {
    setBusy(true); setNote('');
    try { const r = await apiPut('/nqube/ide/settings', s); setS(r.settings); setNote('Saved — applies to the next workspace you open.'); onSaved?.(r.settings); } catch (e) { setNote(e.message); }
    setBusy(false);
  };
  return (
    <div className="nq-ide-settings" data-nova-note="Editor settings for the IDE: theme, autosave, font size, word wrap, minimap, split direction, tab size. Saved per user; apply to the next workspace.">
      <label className="cs-help">Theme
        <select className="cs-input" value={s.theme} onChange={(e) => set('theme', e.target.value)}>
          <option value="dark">Dark</option><option value="light">Light</option><option value="hc">High contrast</option>
        </select>
      </label>
      <label className="cs-help">Font size
        <select className="cs-input" value={s.fontSize} onChange={(e) => set('fontSize', Number(e.target.value))}>
          {[11, 12, 13, 14, 15, 16, 18].map((n) => <option key={n} value={n}>{n}px</option>)}
        </select>
      </label>
      <label className="cs-help">Open to the side
        <select className="cs-input" value={s.splitDirection} onChange={(e) => set('splitDirection', e.target.value)}>
          <option value="right">splits right</option><option value="down">splits down</option>
        </select>
      </label>
      <label className="cs-help">Tab size
        <select className="cs-input" value={s.tabSize} onChange={(e) => set('tabSize', Number(e.target.value))}>
          {[2, 4, 8].map((n) => <option key={n} value={n}>{n}</option>)}
        </select>
      </label>
      <label className="cs-help nq-ide-check"><input type="checkbox" checked={s.autoSave} onChange={(e) => set('autoSave', e.target.checked)} /> autosave (off = save with ⌘S, the way the team asked)</label>
      <label className="cs-help nq-ide-check"><input type="checkbox" checked={s.wordWrap} onChange={(e) => set('wordWrap', e.target.checked)} /> word wrap</label>
      <label className="cs-help nq-ide-check"><input type="checkbox" checked={s.minimap} onChange={(e) => set('minimap', e.target.checked)} /> minimap</label>
      <div style={{ display: 'flex', gap: 8, alignItems: 'center', gridColumn: '1 / -1' }}>
        <button type="button" className="cs-btn cs-btn-outline cs-btn-sm" disabled={busy} onClick={save}>{busy ? 'Saving…' : 'Save settings'}</button>
        {note && <span className="cs-help" style={{ fontSize: 12 }}>{note}</span>}
      </div>
    </div>
  );
}

export default function PipelineIde() {
  const { orgId, workspaceId, pipelineId } = useParams();
  const [params] = useSearchParams();
  const scope = useMemo(() => ({ org: orgId, workspace: workspaceId }), [orgId, workspaceId]);
  const { doc: pipeline } = useEntityDoc('pipeline', scope, pipelineId);
  const base = `/orgs/${orgId}/ws/${workspaceId}/ide`;
  const [info, setInfo] = useState(null);
  const [err, setErr] = useState('');
  const [busy, setBusy] = useState(false);
  const [current, setCurrent] = useState(null);
  const [blockId, setBlockId] = useState(params.get('block') || '');
  const [mode, setMode] = useState('edit');
  const [showSettings, setShowSettings] = useState(false);

  const codeBlocks = useMemo(() => (pipeline?.graph?.blocks || []).filter((b) => b.type === 'code.python' && String(b.config?.repo || '').trim()), [pipeline]);
  useEffect(() => { if (!blockId && codeBlocks.length) setBlockId(codeBlocks[0].id); }, [codeBlocks, blockId]);
  const block = codeBlocks.find((b) => b.id === blockId) || null;

  const load = useCallback(async () => {
    try { const r = await apiGet(base); setInfo(r); if (!r.canEdit) setMode('view'); return r; } catch (e) { setErr(e.message); return null; }
  }, [base]);

  const open = useCallback(async (opts = {}) => {
    setBusy(true); setErr('');
    try {
      const w = await apiPost(`${base}/open`, { pipelineId, blockId: opts.blockId || blockId, mode: opts.mode || mode });
      sessionStorage.setItem(TOKEN_KEY(w.id), w.token);
      setCurrent(w);
      load();
      return w;
    } catch (e) { setErr(e.message); return null; } finally { setBusy(false); }
  }, [base, pipelineId, blockId, mode, load]);

  useEffect(() => {
    let alive = true;
    (async () => {
      const r = await load();
      if (!alive || !r) return;
      const mine = (r.workspaces || []).find((w) => ['starting', 'ready'].includes(w.status) && w.pipelineId === pipelineId && sessionStorage.getItem(TOKEN_KEY(w.id)));
      if (mine) { setCurrent({ ...mine, token: sessionStorage.getItem(TOKEN_KEY(mine.id)) }); if (mine.blockId) setBlockId(mine.blockId); setMode(mine.mode); }
    })();
    return () => { alive = false; };
  }, [load, pipelineId]);

  useEffect(() => {
    if (!current?.id) return undefined;
    let alive = true;
    const tick = async () => {
      try { const w = await apiGet(`${base}/${current.id}`); if (alive) setCurrent((c) => (c && c.id === w.id ? { ...c, ...w } : c)); } catch (e) { if (alive) setErr(e.message); }
    };
    const id = setInterval(tick, current.status === 'ready' ? 30000 : 3000);
    return () => { alive = false; clearInterval(id); };
  }, [base, current?.id, current?.status]);

  const stop = useCallback(async (id) => {
    setBusy(true);
    try { await apiPost(`${base}/${id}/stop`, {}); sessionStorage.removeItem(TOKEN_KEY(id)); if (current?.id === id) setCurrent(null); load(); } catch (e) { setErr(e.message); } finally { setBusy(false); }
  }, [base, current, load]);

  const frameSrc = useMemo(() => (current?.status === 'ready' && current.token ? `${backendBase()}/nqube/ide/${current.id}/w/${current.token}/` : null), [current]);
  const idleLeft = useMemo(() => (current?.status === 'ready' && info?.idleMinutes ? Math.max(0, Math.round(info.idleMinutes - (Date.now() - (current.lastActiveAt || current.readyAt || Date.now())) / 60000)) : null), [current, info]);
  const others = (info?.workspaces || []).filter((w) => ['starting', 'ready'].includes(w.status) && w.id !== current?.id);

  return (
    <div className="cs-page nq-ide-page" style={{ maxWidth: 'none' }} data-nova-note={current ? `IDE workspace on ${current.repo}@${current.ref} (${current.mode}), ${current.status}` : `IDE: open a workspace on one of ${codeBlocks.length} Python block repo(s) of ${pipeline?.name || 'this workflow'}`}>
      <p style={{ margin: 0 }}>
        <Link to={`/orgs/${orgId}/ws/${workspaceId}/pipelines/${pipelineId}`} className="cs-help" style={{ textDecoration: 'none' }}>← {pipeline?.name || 'Workflow'}</Link>
      </p>
      <div className="nq-ide-head">
        <h1 style={{ margin: 0 }}>IDE</h1>
        <span className="cs-help" style={{ fontSize: 12, margin: 0 }}>
          {info ? <>{info.canEdit ? 'edit and push' : 'read-only'} · idle stop after {info.idleMinutes} min · {info.plugins?.length || 0} preview plugin(s)</> : 'loading…'}
        </span>
        <span style={{ flex: 1 }} />
        <button type="button" className="cs-btn cs-btn-outline cs-btn-sm" onClick={() => setShowSettings((v) => !v)} aria-expanded={showSettings}>Editor settings</button>
        {current && (
          <>
            <StatusTag w={current} />
            <span className="cs-help" style={{ fontSize: 12, margin: 0 }}>{current.mode} · <code>{current.repo}@{String(current.ref).slice(0, 12)}</code>{idleLeft != null ? ` · idle stop in ~${idleLeft} min` : ''}</span>
            {frameSrc && <a className="cs-btn cs-btn-outline cs-btn-sm" href={frameSrc} target="_blank" rel="noopener noreferrer">New tab</a>}
            <button type="button" className="cs-btn cs-btn-outline cs-btn-sm" disabled={busy} onClick={() => stop(current.id)} data-agent-guard="true" title="Stops the workspace; uncommitted work is lost">Stop</button>
          </>
        )}
      </div>
      {err && <p className="cs-help" style={{ color: 'var(--danger, #e5484d)' }} role="alert">{err}</p>}
      {showSettings && <div className="cs-card" style={{ padding: 12, marginTop: 10 }}><SettingsForm /></div>}

      {info && !info.enabled && (
        <div className="cs-empty" style={{ marginTop: 12 }}>IDE workspaces are off on this plane — an operator enables them with <code>scripts/provision-nqube-ide.sh</code> (config/app nqube.ide.enabled).</div>
      )}

      {info?.enabled && !current && (
        <section className="cs-card" style={{ padding: 12, marginTop: 10, maxWidth: 760 }}>
          <strong style={{ color: 'var(--text)', fontSize: 13 }}>Open a workspace</strong>
          <p className="cs-help" style={{ fontSize: 12 }}>
            A full IDE (Eclipse Theia) on a block&apos;s repository at the ref the block runs. {info.canEdit ? 'Commit and push from Source Control or the terminal; git asks nQube for credentials when it needs them. ' : 'Browse files, history and diffs; nothing can be changed or pushed. '}
            Workspaces are ephemeral: stop, {info.idleMinutes} idle minutes, or {info.maxHours} hours of age ends them and uncommitted work is lost.
          </p>
          {codeBlocks.length === 0 ? (
            <div className="cs-empty">No Python block with a repository yet — set <em>Repository</em> on a Python block in the editor, then come back.</div>
          ) : (
            <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'flex-end' }}>
              <label className="cs-help" style={{ fontSize: 12, flex: '1 1 240px' }}>Block
                <select className="cs-input" style={{ width: '100%', marginTop: 4 }} value={blockId} onChange={(e) => setBlockId(e.target.value)}>
                  {codeBlocks.map((b) => <option key={b.id} value={b.id}>{b.label || b.id} · {b.config.repo}@{String(b.config.ref || 'main').slice(0, 12)}</option>)}
                </select>
              </label>
              <label className="cs-help" style={{ fontSize: 12 }}>Mode
                <select className="cs-input" style={{ width: '100%', marginTop: 4 }} value={mode} onChange={(e) => setMode(e.target.value)} disabled={!info.canEdit}>
                  {info.canEdit && <option value="edit">Edit — commit and push</option>}
                  <option value="view">Read-only</option>
                </select>
              </label>
              <button type="button" className="cs-btn cs-btn-primary cs-btn-sm" disabled={busy || !block} onClick={() => open()}>{busy ? 'Opening…' : 'Open workspace'}</button>
            </div>
          )}
        </section>
      )}

      {current?.status === 'starting' && (
        <div className="cs-card" role="status" style={{ padding: 16, marginTop: 10, display: 'flex', gap: 12, alignItems: 'center' }}>
          <span className="nq-light nq-light-running" />
          <div>
            <strong style={{ color: 'var(--text)', fontSize: 13 }}>{PHASE_LABEL[current.phase] || 'Starting'}…</strong>
            <p className="cs-help" style={{ fontSize: 12, margin: '4px 0 0' }}>A cold start pulls the IDE image and clones the repository — usually one to two minutes. The workspace opens here when the IDE answers.</p>
          </div>
        </div>
      )}
      {current?.status === 'error' && (
        <p className="cs-help" style={{ color: 'var(--danger, #e5484d)', marginTop: 10 }} role="alert">
          The workspace failed to start: {current.error || 'unknown error'}{' '}
          <button type="button" className="cs-btn cs-btn-outline cs-btn-sm" onClick={() => { sessionStorage.removeItem(TOKEN_KEY(current.id)); setCurrent(null); }}>Dismiss</button>
        </p>
      )}
      {current && ['stopped', 'stopping'].includes(current.status) && (
        <p className="cs-help" style={{ marginTop: 10 }}>
          This workspace was stopped{current.reason ? ` — ${current.reason}` : ''}.{' '}
          <button type="button" className="cs-btn cs-btn-outline cs-btn-sm" onClick={() => { sessionStorage.removeItem(TOKEN_KEY(current.id)); setCurrent(null); }}>Open another</button>
        </p>
      )}
      {current?.status === 'ready' && !current.token && (
        <p className="cs-help" style={{ marginTop: 10 }}>
          This tab has no key for the running workspace.{' '}
          <button type="button" className="cs-btn cs-btn-outline cs-btn-sm" disabled={busy} onClick={() => open({ mode: current.mode, blockId: current.blockId })}>Reconnect</button>
        </p>
      )}
      {frameSrc && (
        <iframe className="nq-ide-frame" title={`IDE workspace on ${current.repo}`} src={frameSrc} allow="clipboard-read; clipboard-write" data-nova-ignore="true" />
      )}

      {others.length > 0 && (
        <section className="cs-card" style={{ padding: 12, marginTop: 10 }}>
          <strong style={{ color: 'var(--text)', fontSize: 13 }}>Other running workspaces</strong>
          <ul className="nq-filelist" style={{ marginTop: 8 }}>
            {others.map((w) => (
              <li key={w.id}>
                <span>{w.email || w.uid} · <code>{w.repo}@{String(w.ref).slice(0, 10)}</code> · {w.mode} · <StatusTag w={w} /> · <span className="cs-help" style={{ fontSize: 11.5 }}>{ago(w.lastActiveAt)}</span></span>
                {sessionStorage.getItem(TOKEN_KEY(w.id)) && <button type="button" className="cs-btn cs-btn-outline cs-btn-sm" onClick={() => setCurrent({ ...w, token: sessionStorage.getItem(TOKEN_KEY(w.id)) })}>Show</button>}
                <button type="button" className="cs-btn cs-btn-outline cs-btn-sm" disabled={busy} onClick={() => stop(w.id)} data-agent-guard="true">Stop</button>
              </li>
            ))}
          </ul>
        </section>
      )}
    </div>
  );
}
